'use client'

import { type AxiosError } from 'axios'
import { setCookie } from 'cookies-next/client'
import {
  type FC,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Link, LoadingButton, Notification } from 'base-ui'

import { type BackendApiResponseError } from '../../../base-errors'
import { COOKIE_GUEST_EMAIL_NAME } from '../../../cookies'
import { FriendlyCaptchaChallenge } from '../../../friendly-captcha'
import { useGlobalConfigContext } from '../../../global-config'
import { HTTP_STATUS_CODE_BAD_REQUEST } from '../../../http'
import { urlResolverGetLogin } from '../../../url-handling'
import { useOneTimePasswordApiLoginMutation } from '../../api/swr/useOneTimePasswordApiLoginMutation'
import {
  LOGIN_OTP_STATE,
  LoginRegistrationUserLoginStateContext,
} from '../../context/LoginRegistrationUserLoginStateContext'
import {
  type LoginRegistrationOneTimePasswordInputFormState,
  makeLoginRegistrationOneTimePasswordInputFormState,
  OTP_LENGTH,
} from '../../makeLoginRegistrationOneTimePasswordInputFormState'
import { LoginRegistrationOneTimePasswordInput } from '../LoginRegistrationOneTimePasswordInput/LoginRegistrationOneTimePasswordInput'

const WAIT_TIME_FOR_RE_REQUEST_MESSAGE = 30_000

const handleSubmit = async (
  otpSwr: ReturnType<typeof useOneTimePasswordApiLoginMutation>,
  formState: LoginRegistrationOneTimePasswordInputFormState,
): Promise<void> => {
  if (otpSwr.isMutating) {
    return
  }

  formState.validate()
  if (!formState.isValid()) {
    return
  }

  const { model } = formState

  try {
    await otpSwr.trigger({
      fcSolution: model.fcSolution,
      password: model.password,
      username: model.username,
    })
  } catch {
    // void - error is handled in component (mutation error)
  }
}

interface LoginRegistrationOneTimePasswordInputFormProps {
  afterLoggedIn?: (afterLoggedInSideEffectsPromise?: Promise<unknown>) => void
}

export const LoginRegistrationOneTimePasswordInputForm: FC<
  LoginRegistrationOneTimePasswordInputFormProps
  // eslint-disable-next-line max-lines-per-function,max-statements -- should be refactored and split
> = ({ afterLoggedIn }) => {
  const i18n = useIntl()
  const globalConfig = useGlobalConfigContext()

  const { otpQuery, setLoginOtpState, setUsername, username } = useContext(
    LoginRegistrationUserLoginStateContext,
  )

  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null)

  const [isSubmitted, setIsSubmitted] = useState(false)

  const formState = useMemo(() => {
    const state = makeLoginRegistrationOneTimePasswordInputFormState(
      i18n,
      username,
      otpQuery,
    )

    return state
  }, [i18n, otpQuery, username]).use()

  const { model } = formState

  const [isFriendlyCaptchaDone, setIsFriendlyCaptchaDone] =
    useState<boolean>(false)

  const captchaDoneCallback = useCallback(
    (fcSolution: string): void => {
      formState.setValue(fcSolution, model, 'fcSolution')
      setIsFriendlyCaptchaDone(true)
    },
    [formState, model],
  )

  const [showReRequestMessage, setShowReRequestMessage] =
    useState<boolean>(false)

  useEffect(() => {
    setTimeout(() => {
      setShowReRequestMessage(true)
    }, WAIT_TIME_FOR_RE_REQUEST_MESSAGE)
  }, [])

  useEffect(() => {
    if (!username) {
      window.location.href = urlResolverGetLogin(globalConfig)
    }
  }, [username, globalConfig])

  const [showGeneralErrorNotification, setShowGeneralErrorNotification] =
    useState(false)

  const otpSwr = useOneTimePasswordApiLoginMutation({
    onError: (error: AxiosError<BackendApiResponseError>) => {
      if (error.response?.status === HTTP_STATUS_CODE_BAD_REQUEST) {
        setApiErrorMessage(i18n.formatMessage({ id: 'login.otp.wrongCode' }))
      } else {
        setShowGeneralErrorNotification(true)
      }
      formState.setValue('', model, 'fcSolution')
      setIsSubmitted(false)
    },
    onSuccess: afterLoggedIn,
  })

  const codeRequestLink = (chunks: ReactNode): JSX.Element => (
    <Link
      className="cursor-pointer underline hover:text-dark-primary-max"
      data-qa-id="otp-re-request-code"
      onClick={(event) => {
        event.preventDefault()
        setLoginOtpState(LOGIN_OTP_STATE.OTP_REQUEST_NEW_CODE)
      }}
    >
      {chunks}
    </Link>
  )

  return (
    <>
      <h2 className="mb-2 text-l font-medium">
        <FormattedMessage id="login.otp.withCode" />
      </h2>
      {showGeneralErrorNotification ? (
        <Notification severity="error">
          {i18n.formatMessage({ id: 'general.error' })}
        </Notification>
      ) : null}
      <p className="mb-4 text-m font-normal">
        <FormattedMessage
          id="login.otp.emailSent"
          values={{
            email: username,
          }}
        />
      </p>
      <form
        action={urlResolverGetLogin(globalConfig)}
        data-clientside-hook="formValidation"
        method="POST"
        name="otp-login-form"
        onSubmit={(event): void => {
          event.preventDefault()
          setShowGeneralErrorNotification(false)
          setApiErrorMessage(null)
          setIsSubmitted(true)
          void handleSubmit(otpSwr, formState)
        }}
      >
        <div className="mb-5 rounded-2xl bg-light-primary-medium p-4">
          <h3 className="mb-4 text-m font-bold">
            <FormattedMessage id="login.otp.enterCodeHere" />
          </h3>
          <LoginRegistrationOneTimePasswordInput
            defaultValue={otpQuery}
            formState={formState}
            hasError={
              Boolean(apiErrorMessage) ||
              Boolean(model.validator.getFirstErrorFor('password'))
            }
            isSubmitted={isSubmitted}
            length={OTP_LENGTH}
          >
            {apiErrorMessage || model.validator.getFirstErrorFor('password') ? (
              <span className="mt-2 text-s text-dark-error-medium">
                {apiErrorMessage ??
                  model.validator.getFirstErrorFor('password')}
              </span>
            ) : null}
          </LoginRegistrationOneTimePasswordInput>
          {showReRequestMessage ? (
            <p className="mb-1 mt-6">
              {i18n.formatMessage(
                { id: 'login.otp.checkSpam' },
                {
                  // eslint-disable-next-line id-length -- l10n parameter
                  a: codeRequestLink,
                },
              )}
            </p>
          ) : null}
        </div>
        <LoadingButton
          className="mb-5 w-full"
          data-qa-id="otp-input-submit-button"
          disabled={!isFriendlyCaptchaDone || otpSwr.isMutating}
          id="otp-submit-btn"
          loading={otpSwr.isMutating}
          name="otp-input-submit-button"
          type="submit"
        >
          <FormattedMessage id="login.otp.sendCode" />
        </LoadingButton>
        <div className="text-center">
          <button
            className="cursor-pointer border-none bg-transparent p-0 text-left text-dark-brand-medium underline hover:text-dark-primary-max"
            data-qa-id="otp-change-email"
            onClick={() => {
              setCookie(COOKIE_GUEST_EMAIL_NAME, '')
              setUsername('')
              setLoginOtpState(LOGIN_OTP_STATE.OTP_REQUEST_NEW_CODE)
            }}
            type="button"
          >
            <FormattedMessage id="login.otp.changeEmail" />
          </button>
        </div>
        <div className="my-4 text-center">
          <button
            className="cursor-pointer border-none bg-transparent p-0 text-left text-dark-brand-medium underline hover:text-dark-primary-max"
            data-qa-id="otp-back-to-login-with-password"
            onClick={() => {
              setLoginOtpState(LOGIN_OTP_STATE.LOGIN_REGISTER)
            }}
            type="button"
          >
            <FormattedMessage id="login.otp.backToLoginWithPassword" />
          </button>
        </div>
        <FriendlyCaptchaChallenge
          doneCallback={captchaDoneCallback}
          startMode="auto"
          whenThisPropChangesResetCaptcha={otpSwr.error}
        />
      </form>
    </>
  )
}
